import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import './ConfirmationModal.css';

function ConfirmationModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  onCancel = null, // Optional cancel handler
  title, 
  message, 
  confirmText, 
  cancelText,
  type = 'info', // 'info', 'warning', 'success', 'error', 'duplicate'
  details = null, // Object with additional details to display
  icon = null // Custom icon emoji
}) {
  const { t } = useTranslation();
  const confirmLabel = confirmText ?? t('modal.confirm');
  const cancelLabel = cancelText ?? t('modal.cancel');
  if (!isOpen) return null;

  const getIcon = () => {
    if (icon) return icon;
    switch (type) {
      case 'warning': return '⚠️';
      case 'success': return '✅';
      case 'error': return '❌';
      case 'duplicate': return '✨';
      default: return '💡';
    }
  };

  const getTitleColor = () => {
    switch (type) {
      case 'warning': return '#FF6B6B';
      case 'success': return '#4ECDC4';
      case 'error': return '#FF6B6B';
      case 'duplicate': return '#E91E63';
      default: return '#E91E63';
    }
  };

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="confirmation-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="confirmation-modal-content"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="confirmation-modal-header">
              <div className="confirmation-icon-wrapper" style={{ backgroundColor: `${getTitleColor()}15` }}>
                <span className="confirmation-icon">{getIcon()}</span>
              </div>
              <h2 className="confirmation-title" style={{ color: getTitleColor() }}>
                {title}
              </h2>
            </div>

            <div className="confirmation-modal-body">
              <p className="confirmation-message">{message}</p>
              
              {details && (
                <div className="confirmation-details">
                  {details.appointmentName && (
                    <div className="detail-item">
                      <span className="detail-label">📋 {t('modal.detail.appointment')}</span>
                      <span className="detail-value">{details.appointmentName}</span>
                    </div>
                  )}
                  {details.status && (
                    <div className="detail-item">
                      <span className="detail-label">📊 {t('modal.detail.status')}</span>
                      <span className="detail-value">{details.status}</span>
                    </div>
                  )}
                  {details.date && (
                    <div className="detail-item">
                      <span className="detail-label">📅 {t('modal.detail.date')}</span>
                      <span className="detail-value">{details.date}</span>
                    </div>
                  )}
                  {details.week && (
                    <div className="detail-item">
                      <span className="detail-label">📆 {t('modal.detail.week')}:</span>
                      <span className="detail-value">{t('modal.detail.weekValue', { week: details.week })}</span>
                    </div>
                  )}
                  {details.location && (
                    <div className="detail-item">
                      <span className="detail-label">📍 {t('modal.detail.location')}</span>
                      <span className="detail-value">{details.location}</span>
                    </div>
                  )}
                  {details.provider && (
                    <div className="detail-item">
                      <span className="detail-label">👩‍⚕️ {t('modal.detail.provider')}</span>
                      <span className="detail-value">{details.provider}</span>
                    </div>
                  )}
                  {details.count && (
                    <div className="detail-item highlight">
                      <span className="detail-label">✨ {t('modal.detail.count')}</span>
                      <span className="detail-value">{details.count}</span>
                    </div>
                  )}
                  {details.successCount && (
                    <div className="detail-item success">
                      <span className="detail-label">✅ {t('modal.detail.added')}</span>
                      <span className="detail-value">{details.successCount}</span>
                    </div>
                  )}
                  {details.updatedCount && (
                    <div className="detail-item info">
                      <span className="detail-label">🔄 {t('modal.detail.updated')}</span>
                      <span className="detail-value">{details.updatedCount}</span>
                    </div>
                  )}
                  {details.skippedCount && (
                    <div className="detail-item warning">
                      <span className="detail-label">⏭️ {t('modal.detail.skipped')}</span>
                      <span className="detail-value">{details.skippedCount}</span>
                    </div>
                  )}
                  {details.errorCount && (
                    <div className="detail-item error">
                      <span className="detail-label">❌ {t('modal.detail.failed')}</span>
                      <span className="detail-value">{details.errorCount}</span>
                    </div>
                  )}
                  
                  {/* Success List */}
                  {details.successList && details.successList.length > 0 && (
                    <div className="detail-item success" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.5rem' }}>
                      <span className="detail-label">✅ {t('modal.detail.newlyAdded', { count: details.successList.length })}</span>
                      <ul style={{ margin: 0, paddingLeft: '1.5rem', listStyle: 'disc', color: '#3A3055', fontSize: '0.875rem' }}>
                        {details.successList.slice(0, 5).map((item, idx) => (
                          <li key={idx} style={{ marginBottom: '0.25rem' }}>{item}</li>
                        ))}
                        {details.successList.length > 5 && (
                          <li style={{ fontStyle: 'italic', color: '#E91E63' }}>{t('modal.detail.andMore', { count: details.successList.length - 5 })}</li>
                        )}
                      </ul>
                    </div>
                  )}
                  
                  {/* Updated List */}
                  {details.updatedList && details.updatedList.length > 0 && (
                    <div className="detail-item info" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.5rem' }}>
                      <span className="detail-label">🔄 {t('modal.detail.updatedCount', { count: details.updatedList.length })}</span>
                      <ul style={{ margin: 0, paddingLeft: '1.5rem', listStyle: 'disc', color: '#3A3055', fontSize: '0.875rem' }}>
                        {details.updatedList.slice(0, 5).map((item, idx) => (
                          <li key={idx} style={{ marginBottom: '0.25rem' }}>{item}</li>
                        ))}
                        {details.updatedList.length > 5 && (
                          <li style={{ fontStyle: 'italic', color: '#E91E63' }}>{t('modal.detail.andMore', { count: details.updatedList.length - 5 })}</li>
                        )}
                      </ul>
                    </div>
                  )}
                  
                  {/* Skipped List */}
                  {details.skippedList && details.skippedList.length > 0 && (
                    <div className="detail-item warning" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.5rem' }}>
                      <span className="detail-label">⏭️ {t('modal.detail.skippedAlreadyExist', { count: details.skippedList.length })}</span>
                      <ul style={{ margin: 0, paddingLeft: '1.5rem', listStyle: 'disc', color: '#3A3055', fontSize: '0.875rem' }}>
                        {details.skippedList.slice(0, 5).map((item, idx) => (
                          <li key={idx} style={{ marginBottom: '0.25rem' }}>{item}</li>
                        ))}
                        {details.skippedList.length > 5 && (
                          <li style={{ fontStyle: 'italic', color: '#E91E63' }}>{t('modal.detail.andMore', { count: details.skippedList.length - 5 })}</li>
                        )}
                      </ul>
                    </div>
                  )}
                  
                  {/* Error List */}
                  {details.errorList && details.errorList.length > 0 && (
                    <div className="detail-item error" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.5rem' }}>
                      <span className="detail-label">❌ {t('modal.detail.failedCount', { count: details.errorList.length })}</span>
                      <ul style={{ margin: 0, paddingLeft: '1.5rem', listStyle: 'disc', color: '#3A3055', fontSize: '0.875rem' }}>
                        {details.errorList.slice(0, 3).map((item, idx) => (
                          <li key={idx} style={{ marginBottom: '0.25rem' }}>
                            {typeof item === 'string' ? item : `${item.name}: ${item.error}`}
                          </li>
                        ))}
                        {details.errorList.length > 3 && (
                          <li style={{ fontStyle: 'italic', color: '#E91E63' }}>{t('modal.detail.andMore', { count: details.errorList.length - 3 })}</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {type === 'duplicate' && (
                <div className="confirmation-note">
                  <p>💡 <strong>{t('modal.duplicate.whatNext')}</strong></p>
                  <p>{t('modal.duplicate.description')}</p>
                </div>
              )}
            </div>

            <div className="confirmation-modal-footer">
              {cancelLabel && (
              <motion.button
                className="confirmation-btn confirmation-btn-cancel"
                onClick={() => {
                  if (onCancel) {
                    onCancel();
                  }
                  onClose();
                }}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                {cancelLabel}
              </motion.button>
            )}
              <motion.button
                className={`confirmation-btn confirmation-btn-confirm confirmation-btn-${type}`}
                onClick={handleConfirm}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                style={{ backgroundColor: getTitleColor() }}
              >
                {confirmLabel}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
}

export default ConfirmationModal;

